import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService } from '../services/authService';
import { RoleService } from '../services/roleService';

interface PermissionsContextType {
  permissions: string[];
  user: { id: number; email: string } | null;
  loading: boolean;
  error: string | null;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [user, setUser] = useState<{ id: number; email: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if token exists before trying to fetch
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return; // No token, skip fetching permissions
      }

      const user = await authService.getCurrentUser();
      setPermissions(user.permissions || []);
      setUser({ id: user.id, email: user.email });
      setError(null); // Clear any previous errors on success
    } catch (err: any) {
      console.error('Failed to fetch permissions:', err);

      // Only set error message, don't clear permissions/user data
      // This allows the app to continue working even if permissions fail to load
      const errorMessage = err?.message || 'Failed to load user permissions';
      setError(errorMessage);

      // Don't clear permissions/user on error - allow graceful degradation
      // The app can still work with cached permissions or default to no permissions
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshPermissions();
  }, []);

  const hasPermission = (permission: string): boolean => {
    return RoleService.hasPermission(permissions, permission);
  };

  const hasAnyPermission = (requiredPermissions: string[]): boolean => {
    return RoleService.hasAnyPermission(permissions, requiredPermissions);
  };

  const hasAllPermissions = (requiredPermissions: string[]): boolean => {
    return RoleService.hasAllPermissions(permissions, requiredPermissions);
  };

  const value: PermissionsContextType = {
    permissions,
    user,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions,
  };

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

// Higher-order component for permission-based rendering
interface WithPermissionProps {
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  fallback?: ReactNode;
  children: ReactNode;
}

export const WithPermission: React.FC<WithPermissionProps> = ({
  permission,
  permissions = [],
  requireAll = false,
  fallback = null,
  children,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  if (permission) {
    return hasPermission(permission) ? <>{children}</> : <>{fallback}</>;
  }

  if (permissions.length > 0) {
    const hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);

    return hasAccess ? <>{children}</> : <>{fallback}</>;
  }

  return <>{children}</>;
};
